if (!customElements.get('monsga-creator-library')) {
  customElements.define(
    'monsga-creator-library',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.filter = 'all';
        this.sort = 'latest';
        this.desktopInitialCount = Number(this.dataset.initialCount || 6);
        this.mobileInitialCount = Number(this.dataset.mobileInitialCount || 3);
        this.instagramLabel = this.dataset.instagramLabel || 'View on Instagram';
        this.youtubeLabel = this.dataset.youtubeLabel || 'Watch on YouTube';
        this.baseLimit = matchMedia('(max-width: 749px)').matches ? this.mobileInitialCount : this.desktopInitialCount;
        this.limit = this.baseLimit;
        this.grid = this.querySelector('[data-mcl-grid]');
        this.renderCatalog();
        this.cards = [...this.querySelectorAll('[data-mcl-card]')];
        this.dialog = this.querySelector('[data-mcl-dialog]');
        this.dialogBody = this.querySelector('[data-mcl-dialog-body]');
        this.dialogTitle = this.querySelector('[data-mcl-dialog-title]');
        this.dialogFallback = this.querySelector('[data-mcl-dialog-fallback]');
        this.more = this.querySelector('[data-mcl-more]');
        this.lastTrigger = null;
        this.previewTimer = null;
        this.previewCard = null;

        this.querySelectorAll('[data-mcl-filter]').forEach((button) => {
          button.addEventListener('click', () => {
            this.filter = button.dataset.mclFilter;
            this.limit = this.baseLimit;
            this.querySelectorAll('[data-mcl-filter]').forEach((item) => {
              item.setAttribute('aria-pressed', String(item === button));
            });
            this.refresh();
          });
        });

        this.querySelector('[data-mcl-sort]')?.addEventListener('change', (event) => {
          this.sort = event.currentTarget.value;
          this.querySelector('[data-mcl-sort-toggle]')?.setAttribute('aria-pressed', String(this.sort === 'oldest'));
          this.refresh();
        });

        this.querySelector('[data-mcl-sort-toggle]')?.addEventListener('click', (event) => {
          this.sort = this.sort === 'latest' ? 'oldest' : 'latest';
          event.currentTarget.setAttribute('aria-pressed', String(this.sort === 'oldest'));
          const select = this.querySelector('[data-mcl-sort]');
          if (select) select.value = this.sort;
          this.refresh();
        });

        this.querySelectorAll('[data-mcl-play]').forEach((button) => {
          button.addEventListener('click', () => this.open(button));
        });
        this.querySelectorAll('.mcl__media[data-mcl-play]').forEach((button) => {
          const card = button.closest('[data-mcl-card]');
          button.addEventListener('pointerenter', () => this.queuePreview(card));
          button.addEventListener('pointerleave', () => this.stopPreview(card));
        });

        this.more?.addEventListener('click', () => {
          if (this.more.tagName === 'A') return;
          this.limit += this.baseLimit;
          this.refresh();
        });

        this.querySelector('[data-mcl-close]')?.addEventListener('click', () => this.close());
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog?.addEventListener('close', () => this.cleanup());
        this.dialog?.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });

        this.hashHandler = () => this.revealHashTarget();
        window.addEventListener('hashchange', this.hashHandler);

        this.updateCounts();
        this.refresh();
        this.revealHashTarget();
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.hashHandler);
        this.stopPreview();
      }

      renderCatalog() {
        if (!this.grid) return;
        const script = this.querySelector('[data-mcl-catalog]');
        if (!script) return;
        let items = [];
        try {
          items = JSON.parse(script.textContent || '[]');
        } catch (error) {
          console.warn('MONSGA creator catalog could not be parsed.', error);
          return;
        }
        items.forEach((item) => this.grid.append(this.buildCatalogCard(item)));
      }

      buildCatalogCard(item) {
        const card = document.createElement('article');
        card.className = 'mcl__card';
        card.id = `creator-${item.creator_anchor}`;
        card.dataset.mclCard = '';
        card.dataset.category = item.category;
        card.dataset.platform = item.platform;
        card.dataset.order = String(item.order || 0);
        card.dataset.title = item.title || '';
        card.dataset.externalUrl = item.platform_url || '';
        card.dataset.externalLabel = item.platform === 'instagram' ? this.instagramLabel : this.youtubeLabel;

        const media = document.createElement('button');
        media.className = 'mcl__media';
        media.type = 'button';
        media.dataset.mclPlay = '';
        media.setAttribute('aria-label', `Play creator video: ${item.title || ''}`);
        const cover = document.createElement('img');
        const coverCandidates = item.platform === 'youtube' && item.video_id
          ? [
              item.cover_url,
              `https://i.ytimg.com/vi/${item.video_id}/maxresdefault.jpg`,
              `https://i.ytimg.com/vi/${item.video_id}/sddefault.jpg`,
              `https://i.ytimg.com/vi/${item.video_id}/hqdefault.jpg`,
            ].filter(Boolean)
          : [item.cover_url || ''];
        let coverCandidateIndex = 0;
        const useNextCover = () => {
          if (coverCandidateIndex >= coverCandidates.length - 1) return;
          coverCandidateIndex += 1;
          cover.src = coverCandidates[coverCandidateIndex];
        };
        cover.addEventListener('error', useNextCover);
        cover.addEventListener('load', () => {
          if (cover.naturalWidth < 640) useNextCover();
        });
        cover.src = coverCandidates[coverCandidateIndex];
        cover.alt = item.title || '';
        cover.loading = 'lazy';
        cover.width = 1280;
        cover.height = 720;
        media.append(cover, this.platformBadge(item.platform), this.playIcon());
        if (item.duration) {
          const duration = document.createElement('span');
          duration.className = 'mcl__duration';
          duration.textContent = item.duration;
          media.append(duration);
        }

        const copy = document.createElement('div');
        copy.className = 'mcl__copy';
        const creator = document.createElement('div');
        creator.className = 'mcl__creator';
        const avatar = document.createElement('span');
        avatar.className = 'mcl__avatar';
        const avatarImage = document.createElement('img');
        const avatarRemote = item.avatar_url
          ? (item.platform === 'youtube' ? item.avatar_url.replace(/=s\d+-/, '=s176-') : item.avatar_url)
          : '';
        const avatarCandidates = [item.avatar_asset, avatarRemote].filter(Boolean);
        let avatarCandidateIndex = 0;
        avatarImage.src = avatarCandidates[avatarCandidateIndex] || '';
        avatarImage.addEventListener('error', () => {
          if (avatarCandidateIndex < avatarCandidates.length - 1) {
            avatarCandidateIndex += 1;
            avatarImage.src = avatarCandidates[avatarCandidateIndex];
          }
        });
        avatarImage.alt = item.author || '';
        avatarImage.loading = 'lazy';
        avatarImage.width = 180;
        avatarImage.height = 180;
        avatar.append(avatarImage);
        const creatorCopy = document.createElement('span');
        creatorCopy.className = 'mcl__creator-copy';
        const author = document.createElement('strong');
        author.textContent = item.author || '';
        const followers = document.createElement('span');
        followers.textContent = item.followers || '';
        creatorCopy.append(author, followers);
        creator.append(avatar, creatorCopy);
        const title = document.createElement('h3');
        title.textContent = item.title || '';
        const watch = document.createElement('button');
        watch.className = 'mcl__watch';
        watch.type = 'button';
        watch.dataset.mclPlay = '';
        watch.append(
          document.createTextNode(item.platform === 'instagram' ? this.instagramLabel : this.youtubeLabel),
          this.arrowIcon(),
        );
        copy.append(creator, title, watch);

        const player = document.createElement('template');
        player.dataset.mclPlayer = '';
        player.content.append(this.createPlayer(item, false));
        const hover = document.createElement('template');
        hover.dataset.mclHover = '';
        hover.content.append(this.createPlayer(item, true));
        card.append(media, copy, player, hover);
        return card;
      }

      createPlayer(item, preview) {
        if (item.video_url) {
          const video = document.createElement('video');
          video.src = item.video_url;
          video.autoplay = true;
          video.loop = preview;
          video.muted = preview;
          video.controls = !preview;
          video.playsInline = true;
          video.preload = 'metadata';
          return video;
        }
        const iframe = document.createElement('iframe');
        if (item.video_id) {
          const videoId = String(item.video_id).replace(/[^A-Za-z0-9_-]/g, '');
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1${preview ? '&mute=1&controls=0' : ''}`;
        } else {
          iframe.src = item.embed_url || item.platform_url || '';
        }
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.loading = preview ? 'eager' : 'lazy';
        iframe.title = item.title || 'Creator video';
        iframe.setAttribute('allowfullscreen', '');
        return iframe;
      }

      platformBadge(platform) {
        const badge = document.createElement('span');
        badge.className = `mcl__platform mcl__platform--${platform}`;
        badge.innerHTML = platform === 'instagram'
          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9Zm9.8 1.5a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"/></svg>Instagram'
          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z"/></svg>YouTube';
        return badge;
      }

      playIcon() {
        const icon = document.createElement('span');
        icon.className = 'mcl__play';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 24 24"><path d="m9 6 9 6-9 6Z"/></svg>';
        return icon;
      }

      arrowIcon() {
        const wrapper = document.createElement('span');
        wrapper.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
        return wrapper.firstElementChild;
      }

      updateCounts() {
        const counts = {
          all: this.cards.length,
          youtube: this.cards.filter((card) => card.dataset.platform === 'youtube').length,
          instagram: this.cards.filter((card) => card.dataset.platform === 'instagram').length,
          'robot-vacuums': this.cards.filter((card) => card.dataset.category === 'robot-vacuums').length,
          'cordless-vacuums': this.cards.filter((card) => card.dataset.category === 'cordless-vacuums').length,
        };
        Object.entries(counts).forEach(([key, value]) => {
          const node = this.querySelector(`[data-mcl-count="${key}"]`);
          if (node) node.textContent = `(${value})`;
        });
      }

      queuePreview(card) {
        if (!card || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(() => this.startPreview(card), 160);
      }

      startPreview(card) {
        if (!card || card === this.previewCard) return;
        this.stopPreview();
        const template = card.querySelector('template[data-mcl-hover]');
        const media = card.querySelector('.mcl__media');
        if (!template || !media || !template.content.childElementCount) return;
        const layer = document.createElement('span');
        layer.className = 'mcl__hover-preview';
        layer.dataset.mclHoverActive = '';
        layer.append(template.content.cloneNode(true));
        media.prepend(layer);
        layer.querySelector('video')?.play().catch(() => {});
        this.previewCard = card;
      }

      stopPreview(card) {
        clearTimeout(this.previewTimer);
        if (card && this.previewCard && card !== this.previewCard) return;
        const active = this.previewCard || card;
        active?.querySelectorAll('[data-mcl-hover-active]').forEach((node) => {
          node.querySelectorAll('video').forEach((video) => video.pause());
          node.remove();
        });
        this.previewCard = null;
      }

      matches(card) {
        return this.filter === 'all' || card.dataset.category === this.filter || card.dataset.platform === this.filter;
      }

      sortCards() {
        if (!this.grid) return;
        const direction = this.sort === 'oldest' ? -1 : 1;
        [...this.cards]
          .sort((a, b) => (Number(a.dataset.order) - Number(b.dataset.order)) * direction)
          .forEach((card) => this.grid.append(card));
      }

      refresh() {
        this.stopPreview();
        this.sortCards();
        let visible = 0;
        const orderedCards = [...this.grid.querySelectorAll('[data-mcl-card]')];
        const matches = orderedCards.filter((card) => this.matches(card));
        orderedCards.forEach((card) => {
          const show = this.matches(card) && visible < this.limit;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (this.more && this.more.tagName !== 'A') this.more.hidden = visible >= matches.length;
      }

      revealHashTarget() {
        const targetId = location.hash.slice(1);
        const target = this.cards.find((card) => card.id === targetId);
        if (!target) return;
        if (target.hidden) {
          this.filter = 'all';
          this.limit = this.cards.length;
          this.querySelectorAll('[data-mcl-filter]').forEach((item) => {
            item.setAttribute('aria-pressed', String(item.dataset.mclFilter === 'all'));
          });
          this.refresh();
        }
        requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }

      open(trigger) {
        const card = trigger.closest('[data-mcl-card]');
        const template = card?.querySelector('[data-mcl-player]');
        if (!card || !template || !this.dialog || !this.dialogBody) return;
        this.stopPreview(card);
        this.lastTrigger = trigger;
        this.dialogBody.replaceChildren(template.content.cloneNode(true));
        const video = this.dialogBody.querySelector('video');
        if (video) {
          video.controls = true;
          video.loop = false;
          video.muted = false;
          video.playsInline = true;
          video.setAttribute('controls', '');
          video.setAttribute('playsinline', '');
        }
        if (this.dialogTitle) this.dialogTitle.textContent = card.dataset.title || '';
        const externalUrl = card.dataset.externalUrl || '';
        if (this.dialogFallback) {
          this.dialogFallback.href = externalUrl || '#';
          this.dialogFallback.dataset.visible = String(Boolean(externalUrl));
          const label = card.dataset.externalLabel || 'Watch on the original platform';
          this.dialogFallback.firstChild.textContent = label;
        }
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        video?.play().catch(() => {});
        this.querySelector('[data-mcl-close]')?.focus();
      }

      close() {
        if (!this.dialog) return;
        if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
        else {
          this.dialog.removeAttribute('open');
          this.cleanup();
        }
      }

      cleanup() {
        this.dialogBody?.querySelectorAll('video').forEach((video) => video.pause());
        this.dialogBody?.replaceChildren();
        this.lastTrigger?.focus();
      }
    },
  );
}
